import { QICon_caf_pause_button, QICon_caf_play_button, QIcon_chevron_bold_right } from '@repo/icons/index'
import { useRouter } from 'next/router'
import { LegacyRef, useEffect, useRef, useState } from 'react'
import { CAF_ANALYSIS_TYPE_MAP, HIGHLIGHT_METRIC, MetricsDetailItem } from '../../../../model/caf.interface'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import useRNMsg, { ringleAppNavs } from '../../../../modules/hooks/webview/common/useRNMsg'
import useWebviewNavigation from '../../../../modules/hooks/webview/common/useWebviewNavigation'
import { n_caf, s_caf } from '../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { ICON_ALARM_GRAY } from '../../../../modules/svg/svg'
import urls from '../../../../modules/urls'
import { LESSON_ACTIVITIES_MAP, useLessonsStack } from '../../../../pages/[lang]/webview/lessons'
import ErrorReportPopupComponent from '../../../student/portal/lessons/caf/CAFOverview/popup/CafErrorReportPopup'
import Accordion from '../common/Accordion'
import AudioController from '../common/AudioController'
import AccordionContainer from './AccordionContainer'
import AccordionTitle from './AccordionTitle'

export interface AccordionProps {
  details: MetricsDetailItem[]
  highlightId: number
  tag: number
}

const HighlightDetailListPronunciation = ({ details, highlightId, tag }: AccordionProps) => {
  const router = useRouter()
  const { lessonId } = router.query
  const { t } = useTranslation()
  const audioRef: LegacyRef<HTMLAudioElement> = useRef()

  return (
    <div className="mb-[12px] w-full">
      <AccordionContainer>
        {details?.map((content, idx) => (
          <Accordion
            title={<AccordionTitle type={HIGHLIGHT_METRIC.PRONUNCIATION} content={content} index={idx + 1} tag={''} />}
            key={idx + 'component_item'}
            className="mb-[12px] w-full"
            idx={idx}
          >
            {/* 아코디언 내용 */}
            <article className="mb-[8px] text-gray-700">
              <AccordionUpperContent type={HIGHLIGHT_METRIC.PRONUNCIATION} content={content} />
              {content.main_sentence.audio_url && (
                <AudioController src={content.main_sentence.audio_url} id={idx} audioRef={audioRef} />
              )}
              <ErrorList content={content} />
            </article>

            {/* 하단 도움말(오류제보, 인덱스 표시) */}
            <AccordionBottomContent
              type={HIGHLIGHT_METRIC.PRONUNCIATION}
              contentId={content.id}
              contentSentence={content?.main_sentence.sentence}
              content={content}
              tag={tag}
              highlightId={highlightId}
            />
          </Accordion>
        ))}
      </AccordionContainer>
    </div>
  )
}

export const ErrorList = ({ content }) => {
  if (!content) return
  const errors = content?.main_sentence?.word_error_index || []
  return (
    <div className="mt-[20px] flex flex-col items-start justify-center px-[16px]">
      {errors.map((error, i) => {
        return <ErrorItem syllables={content.syllables} error={error} key={`${error.sentence}_${i}`} />
      })}
    </div>
  )
}
const ErrorItem = ({
  syllables,
  error,
}: {
  syllables: string[]
  error: { right: string; wrong: string; index: number }
}) => {
  const { t } = useTranslation()
  const errorTargetString = syllables[error.index]

  return (
    <div className="flex flex-row items-center justify-start">
      <div>
        {syllables.map((syllable, i) => (
          <span key={`${syllable}_${i}`}>
            {i !== 0 && '•'}
            {i === error.index ? (
              <span className="text-[14px] text-red-400">{syllable}</span>
            ) : (
              <span className="text-[14px] text-gray-500">{syllable}</span>
            )}
          </span>
        ))}
      </div>
      <div className="mx-[16px] h-[20px] w-[1px] bg-gray-200"></div>
      <div className="text-[14px] text-gray-500">{t(n_caf.pronunciation.error(error.right, error.wrong))}</div>
    </div>
  )
}

export const getErrorMarked = (targetText, errorIndexs) => {
  const wrapTextWithSpan = (origin, ac, beforeIndex, index, length) => {
    const before = origin.substring(beforeIndex, index)
    const target = origin.substring(index, index + length)
    const after = origin.substring(index + length)
    const accumulator = `${ac}${before}<span class="text-red-400">${target}</span>`

    return { accumulator, rest: after }
  }
  const reduced = errorIndexs.reduce(
    (ac, cr, i, arr) => {
      const beforeIndex = i === 0 ? 0 : arr[i - 1].index + arr[i - 1].length
      const { accumulator, rest } = wrapTextWithSpan(targetText, ac.before, beforeIndex, cr.index, cr.length)
      return { origin: targetText, before: accumulator, rest }
    },
    { origin: targetText, before: '', rest: '' }
  )
  const applied = reduced.before + reduced.rest
  return applied
}
const AccordionUpperContent = ({ type, content }) => {
  const { t } = useTranslation()
  const syllableString = content?.syllables.join('•')
  const replacedTextWrapperRef = useRef(null)
  const audioRef = useRef(null)
  const audioId = `audio_${content.audio_url}`
  const [isPlaying, setIsPlaying] = useState(false)

  useEffect(() => {
    const targetText = content?.main_sentence.sentence
    const errorIndexs = content?.main_sentence.sentence_error_index

    if (targetText && errorIndexs && replacedTextWrapperRef.current) {
      const applied = getErrorMarked(targetText, errorIndexs)
      replacedTextWrapperRef.current.innerHTML = applied
    }
  }, [content, replacedTextWrapperRef.current])
  const play = () => {
    audioRef?.current.play()
  }
  const pause = () => {
    audioRef?.current.pause()
  }
  const products = {
    [HIGHLIGHT_METRIC.PRONUNCIATION]: (
      <>
        <div className="flex flex-row items-center justify-start ">
          {!isPlaying ? (
            <QICon_caf_play_button width={32} height={32} className="mr-[8px] cursor-pointer" onClick={play} />
          ) : (
            <QICon_caf_pause_button width={32} height={32} className="mr-[8px] cursor-pointer" onClick={pause} />
          )}
          <audio
            src={content.audio_url}
            id={audioId}
            onEnded={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            ref={audioRef}
            className="hidden"
          />
          <div className="text-[18px] text-gray-500">{syllableString}</div>
        </div>
        <div
          className="rounded-8 mt-[12px] bg-gray-50 py-[8px] px-[16px] text-[16px] text-gray-700"
          ref={replacedTextWrapperRef}
        ></div>
      </>
    ),
  }

  return products[type] || null
}

const AccordionBottomContent = ({ type, contentId, contentSentence, content, highlightId, tag }) => {
  const { t } = useTranslation()
  const { sendPostMsg } = useRNMsg()
  const router = useAppRouter()
  const { navigate } = useWebviewNavigation()
  const [sentenceToReport, setSentenceToReport] = useState('')
  const [showPopup, setShowPopup] = useState(false)
  const isMobile = useIsMobile()
  const isStatistic = router.asPath.includes('/ai-analysis') || router.asPath.includes('/statistic')
  const { lessonId, tab, metricIndex } = router.query
  const isWebview = useIsWebview()
  const { useLessonsFlow } = useLessonsStack()
  const { push } = useLessonsFlow()

  const handleClickErrorReport = (contentId, contentSentence) => () => {
    if (isMobile) {
      sendPostMsg({
        nav: ringleAppNavs.cafErrorReport,
        detail_id: contentId,
        detail_content: contentSentence,
        from: 'highlight',
      })
    } else {
      setSentenceToReport(contentSentence)
      setShowPopup(true)
    }
  }
  const showAll = () => {
    if (isWebview) {
      push(LESSON_ACTIVITIES_MAP.AIStatisticHighlightDetailListActivity, {
        highlightId,
        tag,
        errorId: content.id,
      })
    } else if (isMobile) {
      navigate({
        url: urls.webview.caf.analysis.detail(CAF_ANALYSIS_TYPE_MAP[4], 1, 1),
      })
    } else {
      router.push(
        urls.student.newPortal.lessons.reviewDetail.asCafPronunciationMetricDetail(
          lessonId,
          tag,
          highlightId,
          content.id
        )
      )
    }
  }
  const hideViewMore = !content?.sentences || content?.sentences.length <= 1

  const products = {
    [HIGHLIGHT_METRIC.PRONUNCIATION]: (
      <div className="jsutify-between flex w-full flex-row items-center">
        <div
          className="flex h-[25px] w-full cursor-pointer items-center text-[12px] font-medium tracking-wide text-gray-500 sm:text-[14px]"
          onClick={handleClickErrorReport(contentId, contentSentence)}
        >
          <img src={ICON_ALARM_GRAY} alt="alarm" className="mb-4" />
          <div className="ml-2 mt-1">{t(s_caf.highlight.report)}</div>
          <ErrorReportPopupComponent
            content={sentenceToReport}
            type="highlight"
            isShowEditPopup={showPopup}
            setShowEditPopup={setShowPopup}
            id={contentId}
          />
        </div>
        {!isStatistic && !hideViewMore && (
          <div
            className="flex cursor-pointer flex-row items-center justify-end whitespace-nowrap text-[14px] font-bold text-gray-500"
            onClick={showAll}
          >
            {t(n_caf.highlight18.see_all_sentences_btn)}
            <QIcon_chevron_bold_right width={16} height={16} className="ml-[4px]" />
          </div>
        )}
      </div>
    ),
  }

  return products[type] || null
}

export default HighlightDetailListPronunciation
